/**
 * Hook personalizado para gestionar el temporizador de las pruebas
 * 
 * Este hook proporciona funcionalidades completas de cronometraje:
 * - Control de inicio, pausa y reinicio del temporizador
 * - Cálculo de tiempo restante y tiempo transcurrido
 * - Indicadores de tiempo crítico y de advertencia
 * - Formateo visual del tiempo
 * - Callback automático cuando expira el tiempo
 * 
 * @param {boolean} isActive - Si el temporizador debe estar activo
 * @param {Function} onTimeExpired - Callback ejecutado cuando expira el tiempo
 * @returns {Object} Estado y funciones del temporizador
 * @author Ana Lizcano
 * @hook
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  getTestDurationMs, 
  getWarningTimeMs, 
  getCriticalTimeMs, 
  formatTimeRemaining,
  getTimerColor 
} from '../config/testConfig';

const useTestTimer = (isActive = false, onTimeExpired = null) => {
  // Estado del tiempo restante en milisegundos
  const [timeRemaining, setTimeRemaining] = useState(getTestDurationMs());
  // Estado para controlar si el temporizador está corriendo
  const [isRunning, setIsRunning] = useState(false);
  // Referencia al intervalo para poder limpiarlo
  const intervalRef = useRef(null);

  /**
   * Inicia el temporizador
   * Previene múltiples inicializaciones y configura el intervalo de cuenta regresiva
   */
  const startTimer = useCallback(() => {
    // Prevenir inicio múltiple
    if (isRunning) return;
    
    setIsRunning(true);
    
    // Configurar intervalo de cuenta regresiva (cada segundo)
    intervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1000; // Decrementar 1 segundo
        
        // Manejar expiración del tiempo
        if (newTime <= 0) {
          // Detener el temporizador
          setIsRunning(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          // Ejecutar callback de expiración si existe
          if (onTimeExpired) {
            onTimeExpired();
          }
          return 0;
        }
        
        return newTime;
      });
    }, 1000);
  }, [isRunning, onTimeExpired]);

  /**
   * Pausa el temporizador
   * Detiene la cuenta regresiva pero mantiene el tiempo actual
   */
  const pauseTimer = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Reinicia el temporizador
   * Restaura el tiempo inicial y detiene cualquier cuenta regresiva
   */
  const resetTimer = useCallback(() => {
    setTimeRemaining(getTestDurationMs());
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Efecto de limpieza para evitar memory leaks
   * Se ejecuta al desmontar el componente
   */
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  /**
   * Calcula el porcentaje de progreso del temporizador
   * @returns {number} Porcentaje de tiempo transcurrido (0-100)
   */
  const getProgressPercentage = useCallback(() => {
    const elapsed = getTestDurationMs() - timeRemaining;
    return (elapsed / getTestDurationMs()) * 100;
  }, [timeRemaining]);

  /**
   * Verifica si el tiempo restante está en estado crítico
   * @returns {boolean} True si está en tiempo crítico
   */
  const isCriticalTime = useCallback(() => {
    return timeRemaining <= getCriticalTimeMs();
  }, [timeRemaining]);

  /**
   * Verifica si el tiempo restante está en estado de advertencia
   * @returns {boolean} True si está en tiempo de advertencia
   */
  const isWarningTime = useCallback(() => {
    return timeRemaining <= getWarningTimeMs();
  }, [timeRemaining]);

  /**
   * Calcula el tiempo transcurrido desde el inicio
   * @returns {number} Tiempo transcurrido en milisegundos
   */
  const getElapsedTime = useCallback(() => {
    return getTestDurationMs() - timeRemaining;
  }, [timeRemaining]);

  // Retornar todas las propiedades y funciones del temporizador
  return {
    // Estados principales
    timeRemaining,                              // Tiempo restante en ms
    formattedTime: formatTimeRemaining(timeRemaining), // Tiempo formateado (MM:SS)
    timerColor: getTimerColor(timeRemaining),   // Color según el estado del tiempo
    isRunning,                                  // Si está corriendo actualmente
    isTimeExpired: timeRemaining <= 0,          // Si el tiempo ha expirado
    
    // Métricas calculadas
    progressPercentage: getProgressPercentage(), // Porcentaje de progreso
    isCriticalTime: isCriticalTime(),           // Estado crítico
    isWarningTime: isWarningTime(),             // Estado de advertencia
    elapsedTime: getElapsedTime(),              // Tiempo transcurrido
    
    // Funciones de control
    startTimer,   // Iniciar temporizador
    pauseTimer,   // Pausar temporizador
    resetTimer    // Reiniciar temporizador
  };
};

export default useTestTimer;